import { useApplications, useAsync } from '../hooks/usePortal';
import { usePortalContext } from '../hooks/usePortalContext';
import { portal, humanize } from '../lib/portal';
import { normaliseSteps, isDone } from '../lib/steps';
import { Link } from 'react-router-dom';

/**
 * Recurring check-in period, as projected by the BOP monthly check-ins
 * endpoint (BackOfficePrototype server/routes/checkins/monthlyCheckins.ts) and
 * passed through the BFF verbatim. Contract: periodKey, cadence, opensOn,
 * dueOn, closesOn (all non-null ISO), status, submittedAt|null,
 * reviewedAt|null, reviewNotes|null. Only already-opened periods are returned
 * (server filters opensOn <= now). The projection also carries staff-internal
 * `notes` and `reviewedByEmail` — those must NEVER be rendered here.
 * Diff this block against the BOP source on SDK bumps.
 */
interface PeriodView {
  periodKey: string;
  cadence?: 'monthly' | 'quarterly' | 'annual' | string;
  opensOn?: string;
  dueOn?: string;
  closesOn?: string;
  status?: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
}

// Period dates are UTC date boundaries (e.g. 2026-07-01T00:00:00Z) — format
// the UTC calendar date, or negative-offset locales render the previous day.
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' }) : null;

/**
 * Status -> presentation map for the full BOP period status set:
 * pending / submitted / reviewed / flagged / overdue / closed_season_ended.
 * `reviewed` is the approved-equivalent terminal state ('approved' never
 * occurs in the contract). Unknown statuses fall back to a muted pill with
 * humanize(status) so a new server status degrades gracefully. The pill label
 * (humanize(status)) plus the window/submitted/reviewed lines carry all the
 * per-status text — statuses add no extra detail line.
 */
const STATUS_VIEW: Record<string, { pill: string; li: 'done' | 'now' | 'alert'; dot: string }> = {
  pending: { pill: 'pill warn', li: 'now', dot: '•' },
  submitted: { pill: 'pill ok', li: 'done', dot: '✓' },
  reviewed: { pill: 'pill ok', li: 'done', dot: '✓' },
  flagged: { pill: 'pill err', li: 'alert', dot: '!' }, // reviewNotes carry the message
  overdue: { pill: 'pill err', li: 'alert', dot: '!' }, // due date is on the window line
  closed_season_ended: { pill: 'pill muted', li: 'done', dot: '–' },
};

const UNKNOWN_STATUS_VIEW: (typeof STATUS_VIEW)[string] = {
  pill: 'pill muted',
  li: 'now',
  dot: '•',
};

// Object.hasOwn: a status of 'toString'/'constructor' must hit the fallback,
// not resolve an Object.prototype member.
const statusView = (status?: string) => {
  const key = (status ?? '').toLowerCase();
  return Object.hasOwn(STATUS_VIEW, key) ? STATUS_VIEW[key] : UNKNOWN_STATUS_VIEW;
};

/**
 * Check-ins (on-program only — the tab is status-gated in AppLayout).
 *
 * Two live sources, both scoped by the applicant's RESOLVED context:
 *  1. Season/operational-cycle recurring check-ins — fetched for the season
 *     resolved from /me/context (explicit .../season/:seasonId), or via
 *     current-season when Globus should resolve the active cycle itself.
 *  2. Workflow check-in steps from the applicant's own application, which is
 *     pinned to the resolved program + season; these route into the live
 *     form pipeline on /application.
 * If the season cannot be resolved the failure is surfaced — never a
 * defaulted season's check-ins.
 */
export default function CheckInsPage() {
  const { ctx, loading: ctxLoading, error: ctxError } = usePortalContext();
  const { active, loading, error } = useApplications();
  const stepsState = useAsync(() => (active ? portal.journey(active.id) : Promise.resolve(null)), [active?.id]);

  const seasonId = ctx?.season?.id;
  const recurring = useAsync(
    () =>
      ctxLoading || !ctx
        ? Promise.resolve(null)
        : seasonId
          ? portal.checkinsSeason(seasonId)
          : portal.checkinsCurrentSeason(),
    [ctxLoading, seasonId],
  );

  if (ctxLoading || loading || stepsState.loading || recurring.loading) {
    return <div className="card">Loading check-ins…</div>;
  }
  if (ctxError) {
    return <div className="card" role="alert"><p>Your check-ins can't be shown because your program context couldn't be resolved: {ctxError}</p></div>;
  }
  if (error || stepsState.error) {
    return <div className="card" role="alert"><p>{error ?? stepsState.error}</p></div>;
  }

  const periods: PeriodView[] = recurring.data?.periods ?? [];
  // Cadence is per-binding — constant across every period of the season, so
  // it renders ONCE as a chip in the card heading, never per row.
  const cadence: string | undefined = recurring.data?.binding?.cadence ?? periods[0]?.cadence;
  // Card 2 = UNION of the binding's exact stepKey (authoritative link between
  // the recurring check-in and its workflow step — may not match the regex)
  // and the historical title/key regex (still catches check-in-ish steps the
  // binding doesn't cover, e.g. an arrival check-in). Union, not preference:
  // nothing the regex used to surface may disappear because a binding exists.
  const bindingStepKey: string | undefined = recurring.data?.binding?.stepKey;
  const allSteps = normaliseSteps(stepsState.data);
  const isCheckinish = (s: (typeof allSteps)[number]) =>
    /check[-_ ]?in/i.test(s.stepKey) || /check[-_ ]?in/i.test(s.title) || /check[-_ ]?in/i.test(s.phaseKey);
  const stepCheckins = allSteps.filter((s) => s.stepKey === bindingStepKey || isCheckinish(s));

  return (
    <>
      <h1 style={{ marginBottom: 10 }}>Check-ins</h1>

      <div className="card">
        <h2>
          {ctx?.season?.name ? `${ctx.season.name} check-ins` : 'This season'}
          {cadence && <> <span className="pill muted">{humanize(cadence)}</span></>}
        </h2>
        {recurring.error ? (
          <p role="alert">Couldn't load this season's check-ins: {recurring.error}</p>
        ) : periods.length === 0 ? (
          <p>No recurring check-ins are scheduled for your season yet.</p>
        ) : (
          <ol className="journey">
            {periods.map((p) => {
              const view = statusView(p.status);
              // Periods are only returned once opened (server filters
              // opensOn <= now), so the window line reads "Opened", never
              // "Opens".
              const windowLine = [
                p.opensOn && `Opened ${fmtDate(p.opensOn)}`,
                p.closesOn && `closes ${fmtDate(p.closesOn)}`,
                p.dueOn && `Due ${fmtDate(p.dueOn)}`,
              ]
                .filter(Boolean)
                .join(' – ');
              return (
                <li key={p.periodKey} className={view.li}>
                  <span className="dot">{view.dot}</span>
                  <span>
                    <span className="lbl">
                      {humanize(p.periodKey)} <span className={view.pill}>{humanize(p.status)}</span>
                    </span>
                    {windowLine && <div className="sub">{windowLine}</div>}
                    {p.submittedAt && <div className="sub">Submitted {fmtDate(p.submittedAt)}</div>}
                    {p.reviewedAt && <div className="sub">Reviewed {fmtDate(p.reviewedAt)}</div>}
                    {p.reviewNotes && <div className="sub globus-msg">{p.reviewNotes}</div>}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h2>Application check-in steps</h2>
        {stepCheckins.length === 0 ? (
          <p>No check-in steps are due right now. Your monthly check-in will appear here when it opens.</p>
        ) : (
          <ol className="journey">
            {stepCheckins.map((s, i) => (
              <li key={s.stepKey} className={isDone(s) ? 'done' : 'now'}>
                <span className="dot">{isDone(s) ? '✓' : i + 1}</span>
                <span>
                  <span className="lbl">{s.title} {!isDone(s) && <Link to="/application">Complete now</Link>}</span>
                  {s.globusMessage && <div className="sub globus-msg">{s.globusMessage}</div>}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
}
